import { useCallback } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'

import { disabledCalendarDayStyles } from 'components/Common/Calendar/disabledCalendarDayStyles'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import noop from 'lib/function/noop'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

const Root = styled(Clickable)`
  ${disabledCalendarDayStyles}

  height: ${rem(44)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: background-color 0.2s;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: ${rem(12)};
  padding-top: ${rem(4)};

  &.results-calendar {
    height: ${rem(56)};
    margin-top: 0;
    padding-top: 0;
    border-radius: ${props => props.theme.borderRadius.L};

    &.in-range-date {
      border-radius: 0;
    }

    &.range-start {
      border-start-end-radius: 0;
      border-end-end-radius: 0;
    }

    &.range-end {
      border-start-start-radius: 0;
      border-end-start-radius: 0;
    }

    &.other-leg {
      border-radius: ${props => props.theme.borderRadius.L};
    }

    &.other-leg:disabled {
      opacity: 1;
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.in-range-date {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.other-leg {
    border-radius: ${props => props.theme.borderRadius.M};
    box-shadow: inset 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &:not(:disabled):not(.selected) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
        color: ${props => props.theme.palette.neutral.surface.lightContrast};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      color: ${props => props.theme.palette.neutral.surface.lightContrast};
    }
  }
`

const FeaturedIcon = styled(SolidFireIcon)`
  position: absolute;
  top: 0;
  inset-inline-end: ${rem(2)};
`

const PriceCaption = styled(Caption)`
  &:empty {
    display: none;
  }
`

const Label = styled(StructureLabel)`
  position: absolute;
  top: ${rem(-18)};
  inset-inline-start: ${rem(10)};
  z-index: 1;
`

interface Props extends Utils.PropsWithDataAttributes {
  day: Date
  resultsCalendar?: boolean
  rangeStart?: boolean
  rangeEnd?: boolean
  description?: string
  selected?: boolean
  disabled?: boolean
  loading?: boolean
  isInRange?: boolean
  price?: number
  onClick?: (day: Date) => void
  cheapest?: boolean
  featured?: boolean
  label?: string
  /** A date already taken by another multi-city leg, shown outlined rather than filled. */
  otherLeg?: boolean
  /** Where that other leg departs from, shown in place of the price. */
  legAirportCode?: string
}

function FlightsDatePickerDay(props: Props) {
  const {
    day,
    resultsCalendar,
    rangeStart,
    rangeEnd,
    description,
    loading,
    price,
    disabled,
    selected,
    isInRange,
    featured,
    cheapest,
    onClick = noop,
    label,
    otherLeg,
    legAirportCode,
    ...rest
  } = props
  const cheapestPriceColour = useRebrandSuccessDarkColour()

  const onSelectDay = useCallback(() => {
    onClick(day)
  }, [day, onClick])

  return (
    <Root
      {...rest}
      onClick={onSelectDay}
      disabled={disabled}
      className={clsx({
        selected: selected && !loading,
        'results-calendar': resultsCalendar,
        'range-start': resultsCalendar && rangeStart,
        'range-end': resultsCalendar && rangeEnd,
        'in-range-date': isInRange && !loading,
        'other-leg': otherLeg,
      })}
    >
      {label && <Label variant="standard">
        {label}
      </Label>}
      {featured && <FeaturedIcon colour="urgency" size="XS" />}
      <BodyText variant={resultsCalendar ? 'large' : 'medium'}>
        {day.getDate()}
      </BodyText>
      <PriceCaption variant="smallalt" tabletVariant={resultsCalendar ? 'smallalt' : 'medium'} colour={cheapest ? cheapestPriceColour : 'light-foreground-alt'}>
        {legAirportCode}
        {!legAirportCode && loading && !disabled && <TextLoadingBox width="80%" />}
        {!legAirportCode && !loading && !!price && !disabled && <FormatCurrency value={price} />}
      </PriceCaption>
      {description && <BodyText colour="light-foreground-alt" variant="small">{description}</BodyText>}
    </Root>
  )
}

export default FlightsDatePickerDay
